/**
 * useSortableTable Hook
 * 정렬 상태 및 정렬 로직
 */
import { useState, useCallback } from 'react';
import { RiderWorkStatusItem } from '../types/riderWorkStatus.types';

export type SortKey = 'riderId' | 'riderName' | 'totalWorkDays' | 'totalDeliveryCnt';
export type SortDirection = 'asc' | 'desc' | null;

export const useSortableTable = () => {
    const [sortKey, setSortKey] = useState<SortKey | null>(null);
    const [sortDirection, setSortDirection] = useState<SortDirection>(null);

    /**
     * 정렬 핸들러
     */
    const handleSort = useCallback((key: SortKey) => {
        setSortKey((prevKey) => {
            if (prevKey === key) {
                setSortDirection((prevDir) => {
                    if (prevDir === 'asc') return 'desc';
                    // desc -> 초기화
                    setSortKey(null);
                    return null;
                });
                return prevKey;
            }
            setSortDirection('asc');
            return key;
        });
    }, []);

    /**
     * 라이더 목록 정렬
     */
    const sortRiders = useCallback(
        (riders: RiderWorkStatusItem[]): RiderWorkStatusItem[] => {
            if (!sortKey || !sortDirection) return riders;

            return [...riders].sort((a, b) => {
                let aVal: string | number;
                let bVal: string | number;

                switch (sortKey) {
                    case 'riderId':
                        aVal = a.riderId;
                        bVal = b.riderId;
                        break;
                    case 'riderName':
                        aVal = a.riderName;
                        bVal = b.riderName;
                        break;
                    case 'totalWorkDays':
                        aVal = a.totalWorkDays;
                        bVal = b.totalWorkDays;
                        break;
                    case 'totalDeliveryCnt':
                        aVal = a.totalDeliveryCnt ?? 0;
                        bVal = b.totalDeliveryCnt ?? 0;
                        break;
                    default:
                        return 0;
                }

                if (typeof aVal === 'string' && typeof bVal === 'string') {
                    return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
                }

                return sortDirection === 'asc'
                    ? (aVal as number) - (bVal as number)
                    : (bVal as number) - (aVal as number);
            });
        },
        [sortKey, sortDirection],
    );

    return {
        sortKey,
        sortDirection,
        handleSort,
        sortRiders,
    };
};
